import React, { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
} from '@mui/material';

import { CreateColorRequest, createColor } from '~/services/MerchandiseService';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function AddColorDialog({ open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<CreateColorRequest>>({
    name: '',
    parentColor: null,
    hex: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CreateColorRequest, string>>>({});

  const createColorMutation = useMutation({
    mutationFn: (colorData: CreateColorRequest) => createColor(colorData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchandise', 'colors'] });
      enqueueSnackbar('Color created successfully', { variant: 'success' });
      handleClose();
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating color: ${error.message}`, { variant: 'error' });
    },
  });

  const handleClose = () => {
    setFormData({ name: '', parentColor: null, hex: '' });
    setErrors({});
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateColorRequest, string>> = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Name is required';
    }

    if (!formData.hex || formData.hex.trim() === '') {
      newErrors.hex = 'Hex is required';
    } else if (!/^#[0-9A-Fa-f]{6}$/.test(formData.hex)) {
      newErrors.hex = 'Hex must be in format #RRGGBB';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    createColorMutation.mutate({
      name: formData.name!,
      parentColor: formData.parentColor || null,
      hex: formData.hex!,
    });
  };

  const handleFieldChange = (field: keyof CreateColorRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Color</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          <TextField
            label="Name"
            value={formData.name || ''}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
          />

          <TextField
            label="Parent Color"
            value={formData.parentColor || ''}
            onChange={(e) => handleFieldChange('parentColor', e.target.value)}
            error={!!errors.parentColor}
            helperText={errors.parentColor || 'Optional: Name of parent color'}
            fullWidth
          />

          <Box>
            <TextField
              label="Hex"
              value={formData.hex || ''}
              onChange={(e) => handleFieldChange('hex', e.target.value)}
              error={!!errors.hex}
              helperText={errors.hex || 'Format: #RRGGBB (e.g., #FF0000)'}
              fullWidth
              required
              InputProps={{
                startAdornment: formData.hex ? (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: formData.hex,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '4px',
                      }}
                    />
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose} disabled={createColorMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={createColorMutation.isPending}>
          {createColorMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
